import React, { useMemo } from "react";
import { Flex, Tag, Typography } from "antd";
import {
  CheckOutlined,
  MailOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import { useTranslate } from "@refinedev/core";
import { displayEmail } from "@/utilities";
import { fetchCustomerRows } from "./people-filter.api";
import {
  toCustomerCards,
  type CustomerCard,
  type CustomerSelection,
} from "./people-filter.types";
import {
  PEOPLE_FILTER_GREEN,
  PEOPLE_FILTER_GREEN_BG,
  PeopleFilterShell,
} from "./PeopleFilterShell";

const { Text } = Typography;

export interface CustomersFilterProps {
  value?: CustomerSelection[];
  onChange?: (value: CustomerSelection[]) => void;
  /** Los stores elegidos en el dropdown general de la tabla. Vacío = todo lo visible. */
  storeCodes?: string[];
  disabled?: boolean;
  placeholder?: string;
  /** Contactos/Presupuestos/Pedidos quieren los chips dentro del buscador; el dashboard, no. */
  chipsInline?: boolean;
}

/**
 * Filtro de **Clientes**: tarjeta por persona sin badges ni ícono de tipo —no hay nada que
 * diferenciar—, fila resaltada con check al seleccionar, chip simple con el nombre.
 */
export const CustomersFilter: React.FC<CustomersFilterProps> = ({
  value = [],
  onChange,
  storeCodes = [],
  disabled,
  placeholder,
  chipsInline = true,
}) => {
  const t = useTranslate();
  const selected = useMemo(
    () => new Set(value.map((v) => v.contactId)),
    [value],
  );

  const toggle = (card: CustomerCard) => {
    if (selected.has(card.contactId)) {
      onChange?.(value.filter((v) => v.contactId !== card.contactId));
    } else {
      onChange?.([...value, { contactId: card.contactId, name: card.name }]);
    }
  };

  const renderRows = (cards: CustomerCard[]) =>
    cards.map((card) => {
      const active = selected.has(card.contactId);
      return (
        <div
          key={card.contactId}
          role="option"
          aria-selected={active}
          onClick={() => toggle(card)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            cursor: "pointer",
            background: active ? PEOPLE_FILTER_GREEN_BG : "transparent",
            borderBottom: "1px solid #f5f5f5",
          }}
          onMouseEnter={(e) => {
            if (!active) e.currentTarget.style.background = "#fafafa";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = active
              ? PEOPLE_FILTER_GREEN_BG
              : "transparent";
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text
              strong
              style={{
                fontSize: 14,
                display: "block",
                color: active ? PEOPLE_FILTER_GREEN : undefined,
              }}
            >
              {card.name}
            </Text>
            <Flex gap={6} align="center">
              <MailOutlined
                style={{ fontSize: 11, color: "rgba(0,0,0,0.35)" }}
              />
              <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                {displayEmail(card.email) || "–"}
              </Text>
            </Flex>
            {card.telefono && (
              <Flex gap={6} align="center">
                <WhatsAppOutlined
                  style={{ fontSize: 11, color: "rgba(0,0,0,0.35)" }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {card.telefono}
                </Text>
              </Flex>
            )}
          </div>
          {active && (
            <CheckOutlined
              style={{ color: PEOPLE_FILTER_GREEN, flexShrink: 0 }}
            />
          )}
        </div>
      );
    });

  const chips = value.length ? (
    <>
      {value.map((sel) => (
        <Tag
          key={sel.contactId}
          closable
          onClose={(e) => {
            e.preventDefault();
            onChange?.(value.filter((v) => v.contactId !== sel.contactId));
          }}
          style={{ margin: 0 }}
        >
          {sel.name}
        </Tag>
      ))}
    </>
  ) : null;

  return (
    <PeopleFilterShell<CustomerCard>
      placeholder={placeholder ?? t("filter.customers.placeholder")}
      searchPlaceholder={t("filter.people.search")}
      emptyText={t("filter.customers.empty")}
      noResultsText={(q) => t("filter.people.noResults", { q })}
      selectedCount={value.length}
      selectedSummary={t("filter.people.selected", { count: value.length })}
      disabled={disabled}
      fetchKey={storeCodes.join(",")}
      fetchRows={async (search, signal) =>
        toCustomerCards(await fetchCustomerRows({ storeCodes, search, signal }))
      }
      renderRows={renderRows}
      chips={chips}
      chipsInline={chipsInline}
    />
  );
};
